// User API functions for managing user profiles, settings, and business data

const API_BASE = process.env.REACT_APP_BACKEND_API_URL;

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('authToken') || '';
};

// Helper function to make authenticated API calls
const apiCall = async (endpoint, options = {}) => {
  const token = getAuthToken();

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
    },
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Network error' }));
    throw new Error(errorData.message || `HTTP ${response.status}`);
  }

  return response.json();
};

// User Profile API
export const userApi = {
  // Save user profile
  saveProfile: async (userId, profileData) => {
    return apiCall('/api/user/profile', {
      method: 'POST',
      body: JSON.stringify({ userId, profileData }),
    });
  },

  // Get user profile
  getProfile: async (userId) => {
    return apiCall(`/api/user/profile/${userId}`);
  },

  // Update user profile (alias for save)
  updateProfile: async (userId, profileData) => {
    return userApi.saveProfile(userId, profileData);
  },
};

// User Settings API
export const settingsApi = {
  // Save user settings
  saveSettings: async (userId, settings) => {
    return apiCall('/api/user/settings', {
      method: 'POST',
      body: JSON.stringify({ userId, settings }),
    });
  },

  // Get user settings
  getSettings: async (userId) => {
    return apiCall(`/api/user/settings/${userId}`);
  },

  // Update user settings (alias for save)
  updateSettings: async (userId, settings) => {
    return settingsApi.saveSettings(userId, settings);
  },
};

// Business Profile API
export const businessApi = {
  // Save business profile (for new businesses)
  saveProfile: async (userId, businessData) => {
    return apiCall('/api/business/profile', {
      method: 'POST',
      body: JSON.stringify({ userId, businessData }),
    });
  },

  // Get business profile
  getProfile: async (userId) => {
    return apiCall(`/api/business/profile/${userId}`);
  },

  // Update business profile (for existing businesses)
  updateProfile: async (userId, businessData) => {
    return apiCall(`/api/business/profile/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ businessData }),
    });
  },
};

// Combined API for convenience
export const userManagementApi = {
  // Profile management
  saveUserProfile: userApi.saveProfile,
  getUserProfile: userApi.getProfile,
  updateUserProfile: userApi.updateProfile,

  // Settings management
  saveUserSettings: settingsApi.saveSettings,
  getUserSettings: settingsApi.getSettings,
  updateUserSettings: settingsApi.updateSettings,

  // Dhandha Paani
  saveBusinessProfile: businessApi.saveProfile,
  getBusinessProfile: businessApi.getProfile,
  updateBusinessProfile: businessApi.updateProfile,

  // Utility functions
  syncLocalData: async (user) => {
    try {
      // Sync profile data
      const profileResponse = await userApi.getProfile(user.id);
      if (profileResponse.success) {
        localStorage.setItem(`profile_${user.id}`, JSON.stringify(profileResponse.profile));
      }

      // Sync settings data
      const settingsResponse = await settingsApi.getSettings(user.id);
      if (settingsResponse.success) {
        localStorage.setItem(`settings_${user.id}`, JSON.stringify(settingsResponse.settings));
      }

      // Sync business data
      const businessResponse = await businessApi.getProfile(user.id);
      if (businessResponse.success) {
        localStorage.setItem(`business_${user.id}`, JSON.stringify(businessResponse.business));
      }

      return { success: true, message: 'Data synced successfully' };
    } catch (error) {
      
      return { success: false, error: error.message };
    }
  },

  saveAllLocalData: async (user) => {
    try {
      const results = [];

      // Save profile if exists
      const profileData = localStorage.getItem(`profile_${user.id}`);
      if (profileData) {
        const profile = JSON.parse(profileData);
        const result = await userApi.saveProfile(user.id, profile);
        results.push({ type: 'profile', ...result });
      }

      // Save settings if exists
      const settingsData = localStorage.getItem(`settings_${user.id}`);
      if (settingsData) {
        const settings = JSON.parse(settingsData);
        const result = await settingsApi.saveSettings(user.id, settings);
        results.push({ type: 'settings', ...result });
      }

      // Save business if exists
      const businessData = localStorage.getItem(`business_${user.id}`);
      if (businessData) {
        const business = JSON.parse(businessData);
        const result = await businessApi.saveProfile(user.id, business);
        results.push({ type: 'business', ...result });
      }

      return { success: true, results, message: 'All data saved to server' };
    } catch (error) {
      
      return { success: false, error: error.message };
    }
  },
};

export default userManagementApi;
